import MenuOptions from '@/json/menuOptions.json';

export function normalizeRouteKey(path) {
  if (!path) return '/';

  const withLeadingSlash = path.startsWith('/') ? path : `/${path}`;
  const trimmed = withLeadingSlash.replace(/\/+$/, '');

  return trimmed || '/';
}

export function matchesRoutePattern(path, pattern) {
  const normalizedPath = normalizeRouteKey(path);
  const normalizedPattern = normalizeRouteKey(pattern);

  if (normalizedPattern === '/') {
    return normalizedPath === '/';
  }

  return (
    normalizedPath === normalizedPattern ||
    normalizedPath.startsWith(`${normalizedPattern}/`)
  );
}

function uniqueRoutes(routes = []) {
  return [...new Set(routes.map(normalizeRouteKey).filter(Boolean))];
}

export function hasMySmartAnalyticsAccess(
  availableCountries = [],
  company = ''
) {
  // El backend entrega la categoría de Power BI por prefijo de cliente,
  // así que centralizamos aquí la misma regla que usan menú y dashboard.
  const companyPrefix = String(company || '').substring(0, 4);

  return availableCountries.some(
    (country) => country.category === companyPrefix && country.active
  );
}

export function hasNutrivisorAccess({
  hasAccessToAdvanceService = false,
  hasAccessToNutritionalMonitoring = false,
} = {}) {
  // Nutrivisor y Nutritional Monitoring se consideran parte del mismo
  // permiso funcional: acceso a avanzados o a monitoreo nutricional.
  return (
    hasAccessToAdvanceService || hasAccessToNutritionalMonitoring
  );
}

export function hasComplaintManagementAccess({
  enabled = false,
  authenticatedUser = {},
  company = '',
} = {}) {
  if (!company) {
    return false;
  }

  // En Dashboard y en el menu, los empleados AGQ pueden consultar el
  // contador, listado y detalle de incidencias en modo lectura. El boton de
  // alta se controla aparte con hasComplaintRegisterAccess.
  if (authenticatedUser?.is_agq) {
    return true;
  }

  if (!enabled) {
    return false;
  }

  const contacts = authenticatedUser?.contacts || [];

  if (!contacts.length) {
    return false;
  }

  return contacts.some((contact) =>
    (contact?.companies || []).some(
      (contactCompany) => contactCompany?.code === company
    )
  );
}

export function hasComplaintRegisterAccess({
  enabled = false,
  authenticatedUser = {},
  company = '',
} = {}) {
  if (!enabled || authenticatedUser?.is_agq || !company) {
    return false;
  }

  // El alta se concede solo cuando el usuario esta en modo contacto
  // (`enabled`) y la empresa seleccionada pertenece a alguno de sus
  // contactos. Un empleado AGQ nunca puede registrar, aunque otro estado
  // de la interfaz indique que tiene acceso a la empresa.
  const contacts = authenticatedUser?.contacts || [];

  if (!contacts.length) {
    return false;
  }

  return contacts.some((contact) =>
    (contact?.companies || []).some(
      (contactCompany) => contactCompany?.code === company
    )
  );
}

function collectMenuRoutes() {
  const routes = [];
  const contexts = MenuOptions?.contexts || {};

  (MenuOptions?.home?.items || []).forEach((item) => {
    if (item?.url) {
      routes.push(item.url);
    }
  });

  Object.values(contexts).forEach((context) => {
    (context?.routes || []).forEach((route) => routes.push(route));
    (context?.tabs || []).forEach((tab) => {
      if (tab?.url) {
        routes.push(tab.url);
      }
    });
    (context?.items || []).forEach((item) => {
      if (item?.url) {
        routes.push(item.url);
      }
    });

    Object.values(context?.itemsByArea || {}).forEach((items) => {
      (items || []).forEach((item) => {
        if (item?.url) {
          routes.push(item.url);
        }
      });
    });

    Object.values(context?.subMenu || {}).forEach((items) => {
      (items || []).forEach((item) => {
        if (item?.url) {
          routes.push(item.url);
        }
      });
    });
  });

  return uniqueRoutes(routes);
}

export const routeAccessConfig = {
  public: ['/legal-documents', '/legal-notice'],
  legalInternal: [
    '/legal-documents-internal',
    '/legal-notice-internal',
  ],
  // Rutas existentes en la app pero bloqueadas temporalmente en produccion.
  // Deben quedar fuera del menu y tambien del acceso directo por URL.
  blocked: [
    '/ServiceCatalog/',
    '/ServiceCatalogDetails/',
    '/food/risk-analysis',
  ],
  // Rutas "base" que salen del propio menuOptions:
  // home, contextos, tabs, submenus e items visibles.
  general: collectMenuRoutes(),
  profile: ['/profile'],
  clientAdmin: [
    '/client-area',
    '/client-area/contact-management',
    '/contacts/new',
  ],
  materialRequest: ['/MaterialRequest/', '/MaterialRequester/'],
  alerts: ['/alerts'],
  notifications: ['/notifications'],
  complaintsRead: [
    '/ComplaintManagement/',
    '/complaintManagementDetails',
  ],
  complaintsRegister: ['/complaintRegister'],
};

routeAccessConfig.complaints = [
  ...routeAccessConfig.complaintsRead,
  ...routeAccessConfig.complaintsRegister,
];

export const blockedRoutePatterns = [...routeAccessConfig.blocked];

const restrictedRoutePatterns = [
  ...blockedRoutePatterns,
  ...routeAccessConfig.profile,
  ...routeAccessConfig.clientAdmin,
  ...routeAccessConfig.materialRequest,
  ...routeAccessConfig.alerts,
  ...routeAccessConfig.notifications,
  ...routeAccessConfig.complaints,
];

const generalRoutesWithoutRestrictedSections =
  routeAccessConfig.general.filter(
    (route) =>
      !restrictedRoutePatterns.some((pattern) =>
        matchesRoutePattern(route, pattern)
      )
  );

export function getAllowedRoutesForUser({
  isEmployee = false,
  isContact = false,
  isAdmin = false,
  canAccessMaterialRequest = false,
  canAccessComplaints = false,
  canAccessComplaintRegister = canAccessComplaints,
} = {}) {
  // Partimos siempre del bloque comun y luego sumamos las secciones
  // que dependen del tipo de usuario logueado.
  const allowedRoutes = [
    ...generalRoutesWithoutRestrictedSections,
    ...routeAccessConfig.legalInternal,
  ];

  if (isEmployee) {
    // El widget de Dashboard y la opcion del menu comparten el permiso de
    // lectura: un empleado AGQ puede ver contadores, listado y detalle de
    // incidencias, aunque no pueda registrar nuevas.
    if (canAccessComplaints) {
      allowedRoutes.push(...routeAccessConfig.complaintsRead);
    }

    return uniqueRoutes(allowedRoutes);
  }

  if (isContact) {
    allowedRoutes.push(...routeAccessConfig.profile);
    allowedRoutes.push(...routeAccessConfig.alerts);
    allowedRoutes.push(...routeAccessConfig.notifications);
    if (canAccessComplaints) {
      allowedRoutes.push(...routeAccessConfig.complaintsRead);
    }

    if (canAccessComplaintRegister) {
      allowedRoutes.push(...routeAccessConfig.complaintsRegister);
    }

    if (canAccessMaterialRequest) {
      allowedRoutes.push(...routeAccessConfig.materialRequest);
    }

    if (isAdmin) {
      allowedRoutes.push(...routeAccessConfig.clientAdmin);
    }
  }

  return uniqueRoutes(allowedRoutes);
}

export function isPublicRoute(path) {
  return routeAccessConfig.public.some((pattern) =>
    matchesRoutePattern(path, pattern)
  );
}

export function isRouteAllowed(path, allowedRoutes = []) {
  if (
    blockedRoutePatterns.some((pattern) =>
      matchesRoutePattern(path, pattern)
    )
  ) {
    return false;
  }

  if (isPublicRoute(path)) {
    return true;
  }

  return allowedRoutes.some((pattern) =>
    matchesRoutePattern(path, pattern)
  );
}

export function hasContextualRouteAccess(
  path,
  {
    enabled = false,
    hasAccessToAdvanceService = false,
    hasAccessToNutritionalMonitoring = false,
    availableCountries = [],
    company = '',
  } = {}
) {
  const normalizedPath = normalizeRouteKey(path);

  if (
    blockedRoutePatterns.some((pattern) =>
      matchesRoutePattern(normalizedPath, pattern)
    )
  ) {
    return false;
  }

  // Servicios avanzados: mantenemos la regla que ya existia en la app.
  // Un empleado AGQ puede entrar; un contacto solo si realmente tiene acceso.
  if (
    matchesRoutePattern(normalizedPath, '/agronomy/advanced-services')
  ) {
    return !enabled || hasAccessToAdvanceService;
  }

  // Nutritional Monitoring y NutriVisor comparten la misma validacion
  // que ya aplicaba el submenu: si no hay acceso ni a avanzados ni a
  // nutritional monitoring, no se debe poder entrar por URL directa.
  if (
    matchesRoutePattern(
      normalizedPath,
      '/agronomy/nutritionalMonitoring'
    ) ||
    matchesRoutePattern(normalizedPath, '/agronomy/NutriVisor')
  ) {
    return hasNutrivisorAccess({
      hasAccessToAdvanceService,
      hasAccessToNutritionalMonitoring,
    });
  }

  // My Smart Analytics se sigue limitando por pais disponible,
  // igual que hacia el menu lateral.
  if (matchesRoutePattern(normalizedPath, '/food/StatsQueries')) {
    return hasMySmartAnalyticsAccess(availableCountries, company);
  }

  return true;
}
